<template>
  <div class="plegable" :class="{ 'plegable-caja': caja, 'plegable-sub': sub }">
    <!-- La cabecera ES el control: un `button` y no un `div` con `@click`, para que se pueda plegar
         con el teclado y para que un lector de pantalla diga que está plegado o desplegado
         (`aria-expanded`). Con un `div` hay que pulsarlo con el ratón, que es justo lo que dirección
         señaló del selector de la #70. -->
    <button type="button" class="plegable-cabecera" :aria-expanded="abierto ? 'true' : 'false'"
      @click="$emit('alternar')">
      <!-- EL TRIÁNGULO · dibujado con bordes de CSS y no con un carácter ni con un icono de fuente.
           Dirección pidió «un triangulito» apuntando hacia abajo cuando está abierto y hacia la
           derecha cuando está cerrado, y eso tiene que verse igual en los cuatro sitios donde corre
           esta aplicación: un glifo (▾ ▸) depende de la fuente del sistema y en un Android sin esa
           cobertura sale un rectángulo vacío, que es un control sin estado a la vista. -->
      <span class="plegable-triangulo" :class="{ 'plegable-triangulo-cerrado': !abierto }"
        aria-hidden="true"></span>
      <span class="plegable-titulo">
        {{ titulo }}
        <span class="plegable-nota" v-if="nota">{{ nota }}</span>
      </span>
      <!-- LO ESENCIAL DEL BLOQUE CERRADO · la regla de dirección del 20/10/2026: «un bloque cerrado
           no se queda mudo». Solo cuando está cerrado: abierto, la cifra se lee dentro del bloque y
           escribirla también aquí sería la misma cifra dos veces en la misma pantalla. -->
      <span class="plegable-resumen" v-if="!abierto && resumen">{{ resumen }}</span>
      <!-- EL INDICADOR DE CARGA, LOCAL · solo lo pinta quien se lo pasa, o sea los bloques que la
           lectura en curso puede cambiar. Va en la cabecera y no tapando el bloque: la cifra de
           antes se sigue leyendo mientras llega la nueva. -->
      <span class="plegable-refrescando" v-if="refrescando">Actualizando…</span>
    </button>
    <!-- `v-show` y NO `v-if`: cerrar un bloque solo lo oculta. Con `v-if` se desmontaría lo que hay
         dentro, y lo que hay dentro de algunos bloques tiene estado propio —un desplegable abierto,
         un periodo elegido— que volvería a su valor de partida al abrirlo otra vez. Y eso sería
         cambiar un dato por plegar un bloque, que es lo que dirección prohibió. -->
    <div v-show="abierto" class="plegable-cuerpo"
      :class="{ 'plegable-cuerpo-refrescando': refrescando }">
      <slot></slot>
    </div>
  </div>
</template>

<script>
/**
 * **Un bloque que se plega y se despliega con un triangulito** (#749).
 *
 * Dirección lo pidió el 20/10/2026 al probar el Centro de Cobros: la pantalla es larga y cada grupo
 * de información tiene que poder cerrarse, con el triángulo apuntando hacia abajo cuando está
 * abierto y hacia la derecha cuando está cerrado.
 *
 * Es un componente y no una cabecera copiada en cada bloque porque las reglas que dirección fijó son
 * del mecanismo, no de un bloque: la cabecera cerrada dice la cifra clave, cerrar solo oculta, y el
 * indicador de carga va junto a lo que se está actualizando. Copiadas, la cuarta cabecera que
 * alguien escriba se dejará una.
 *
 * **No guarda el estado ni lo decide**: lo recibe en `abierto` y avisa de que se ha pulsado con
 * `alternar`. Quien lo use es el dueño del estado, y así una pantalla con bloques en varios
 * componentes tiene un solo sitio donde se recuerda (ver `plegadoDeBloques`).
 */
export default {
  name: 'BloquePlegable',
  props: {
    /** El rótulo del bloque. Es el que ya tenía: el triángulo se añade, no sustituye a nada. */
    titulo: { type: String, required: true },
    abierto: { type: Boolean, default: true },
    /**
     * La cifra clave del bloque, en una línea, para cuando está cerrado.
     *
     * La escribe quien usa el componente y con los **mismos campos** que pinta el bloque por
     * dentro: dos partes de la pantalla no pueden decir lo contrario la una de la otra, ni abiertas
     * ni cerradas.
     */
    resumen: { type: String, default: '' },
    /** La coletilla pequeña del rótulo («· toda la cartera que ves, sin los filtros de abajo»). */
    nota: { type: String, default: '' },
    /** Si esta lectura puede cambiar lo que el bloque enseña, y por tanto si se avisa aquí. */
    refrescando: { type: Boolean, default: false },
    /**
     * Si el bloque pinta su propia caja. Con `false` no dibuja nada alrededor, que es lo que hace
     * falta para plegar un grupo que **ya tiene** su forma —las tarjetas de cabecera— o que vive
     * dentro de otro bloque: envolverlo en una caja nueva movería de sitio algo que ya funcionaba.
     */
    caja: { type: Boolean, default: true },
    /** Un bloque dentro de otro: se rotula como subtítulo, no como título de bloque. */
    sub: { type: Boolean, default: false }
  }
};
</script>

<style scoped>
/* La caja es la misma que la de los bloques que ya había en las pantallas (`.bloque`): el bloque
   plegable sustituye a la cabecera, no al bloque, así que no puede verse distinto del de al lado. */
.plegable-caja {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  margin: 12px 16px 0;
  padding: 12px 14px;
}

.plegable-cabecera {
  display: flex;
  /* En un móvil, un título con nota larga («Las personas · …») no cabe en una línea con el resumen:
     sin `wrap` el título empujaba el resumen y el «Actualizando…» fuera de la cabecera, y el bloque
     cerrado se quedaba mudo justo donde menos sitio hay. Con `wrap` bajan a la línea siguiente. */
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 2px 0;
  border: 0;
  background: none;
  text-align: left;
  cursor: pointer;
  /* `font-family: inherit` porque un `<button>` no la hereda del documento: sin esto el rótulo del
     bloque saldría con la tipografía del sistema y los títulos de la pantalla no se verían iguales
     entre sí. El tamaño y el peso son los del `.bloque-titulo` al que sustituye. */
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #1a2744;
}
/* Quien navega con el teclado tiene que ver dónde está, igual que en la entrada al informe de
   abonos de esta misma pantalla. */
.plegable-cabecera:focus-visible { outline: 2px solid #185FA5; outline-offset: 2px; }

/* El título puede encogerse y partirse en dos líneas: si no cabe ni solo, que se lea entero antes
   que salirse por la derecha. */
.plegable-titulo { flex: 0 1 auto; min-width: 0; }
.plegable-sub .plegable-cabecera { font-size: 12px; color: #4a5a78; }

.plegable-nota { font-size: 11px; color: #7c8db0; font-weight: 400; margin-left: 4px; }

/* El resumen se queda con el hueco que sobra y se recorta con puntos suspensivos antes que empujar
   el título o el indicador fuera de la pantalla: lo que no puede faltar es el rótulo del bloque.
   La base de 10em es lo que decide cuándo baja de línea: con menos hueco que eso al lado del título
   se leería solo «Pendie…», así que pasa a la línea de abajo con todo el ancho. */
.plegable-resumen {
  flex: 1 1 10em;
  min-width: 0;
  font-size: 11px;
  font-weight: 400;
  color: #4a5a78;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plegable-refrescando {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 400;
  color: #185FA5;
  animation: plegable-latido 1.1s ease-in-out infinite;
}
@keyframes plegable-latido {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
/* Mientras se actualiza, lo de antes sigue legible pero atenuado: la cifra que se está leyendo es
   la anterior y eso tiene que notarse sin que desaparezca. Las medias tintas de la opacidad no se
   bajan más porque una cifra que no se puede leer es lo mismo que una pantalla en blanco. */
.plegable-cuerpo-refrescando { opacity: 0.6; }

/* El mismo hueco que dejaba el `margin-bottom` del `.bloque-titulo` al que sustituye esta cabecera.
   Cerrado no deja hueco ninguno: `v-show` lo oculta con `display: none` y un elemento oculto no
   aporta su margen.

   Y NO en el caso sin caja y sin subtítulo, que es el de un grupo que ya traía su propio hueco —la
   rejilla de tarjetas del Centro de Cobros lo lleva en su `padding`—: ahí el margen de aquí se
   sumaría al suyo y separaría el rótulo de lo que rotula. */
.plegable-caja .plegable-cuerpo { margin-top: 8px; }
.plegable-sub .plegable-cuerpo { margin-top: 6px; }

/* EL TRIÁNGULO · abierto apunta hacia abajo (un borde superior relleno y los lados transparentes),
   cerrado apunta a la derecha (borde izquierdo relleno). El mismo tamaño en los dos estados para que
   el rótulo no se mueva un pixel al plegar. */
.plegable-triangulo {
  flex-shrink: 0;
  width: 0;
  height: 0;
  margin-right: 2px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 7px solid #4a5a78;
}
.plegable-triangulo-cerrado {
  border-left: 7px solid #4a5a78;
  border-right: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
}
</style>
